import React from 'react';

/**
 * A message box that grows with what you type, up to a ceiling.
 *
 * The chat composers were single-line `<input>`s: anything longer than the box
 * scrolled sideways out of view, so you could not read back a message before
 * sending it. This wraps instead and gets taller line by line — then scrolls
 * internally rather than pushing the conversation off the screen.
 *
 * ENTER STILL SENDS, because that is what these composers already did and what
 * every web chat does. Shift+Enter is the newline — which is what makes a
 * multi-line box typable at all rather than merely possible to paste into.
 *
 * The height comes from the element's own `scrollHeight` — what the text
 * ACTUALLY measures — rather than from counting characters, so wrapping, pasted
 * text and emoji are all handled by the thing that laid them out. The ceiling
 * is read from the computed line-height for the same reason: it tracks the font
 * and the user's text-size setting instead of hard-coding a pixel count.
 *
 * Mirrors the mobile app's `GrowingInput`, down to the five-line ceiling.
 */
export function GrowingTextarea({
  value,
  onChange,
  onSend,
  maxLines = 5,
  ...rest
}: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'rows'> & {
  value: string;
  onChange: (value: string) => void;
  /** Enter (without Shift) calls this. */
  onSend?: () => void;
  maxLines?: number;
}) {
  const ref = React.useRef<HTMLTextAreaElement>(null);

  const resize = React.useCallback(() => {
    const element = ref.current;
    if (!element) return;

    // Collapse first: scrollHeight only shrinks back if the box is not already
    // holding itself open at the taller size.
    element.style.height = 'auto';

    const computed = window.getComputedStyle(element);
    const lineHeight = Number.parseFloat(computed.lineHeight) || 20;
    const frame =
      Number.parseFloat(computed.paddingTop) +
      Number.parseFloat(computed.paddingBottom) +
      Number.parseFloat(computed.borderTopWidth) +
      Number.parseFloat(computed.borderBottomWidth);
    const ceiling = lineHeight * maxLines + frame;

    element.style.height = `${Math.min(element.scrollHeight, ceiling)}px`;
    // Only scrollable once there is more text than the box will ever show —
    // below that a scrollbar would appear during a resize and flicker.
    element.style.overflowY = element.scrollHeight > ceiling ? 'auto' : 'hidden';
  }, [maxLines]);

  // On `value`, not on typing: this also catches the box being cleared after a
  // send, where the old text's height would otherwise persist as an empty tall
  // box, and a value set from outside (a suggested prompt being filled in).
  React.useEffect(resize, [value, resize]);

  return (
    <textarea
      {...rest}
      ref={ref}
      rows={1}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' || event.shiftKey) return;
        event.preventDefault();
        onSend?.();
      }}
    />
  );
}
